import React from "react";
import { FormattedMessage } from "react-intl";

export type EmailTemplateLibraryItem = {
  id: string;
  name: string;
  updatedAt: string;
  accent: string;
  filterType?: string;
};

export type EmailTemplateLibraryFilter = {
  id: string;
  label: React.ReactNode;
};

type Props = {
  eyebrow: React.ReactNode;
  title: React.ReactNode;
  subtitle: React.ReactNode;
  statsLabel: React.ReactNode;
  statsCount?: number;
  templates: EmailTemplateLibraryItem[];
  filters: EmailTemplateLibraryFilter[];
  activeFilter: string;
  onFilterChange: (filterId: string) => void;
  createLabel: React.ReactNode;
  onCreate: () => void;
  actionLabel: React.ReactNode;
  onTemplateAction: (templateId: string) => void;
  emptyLabel: React.ReactNode;
  toolbarExtra?: React.ReactNode;
};

export const EmailTemplateLibrary = ({
  eyebrow,
  title,
  subtitle,
  statsLabel,
  statsCount,
  templates,
  filters,
  activeFilter,
  onFilterChange,
  createLabel,
  onCreate,
  actionLabel,
  onTemplateAction,
  emptyLabel,
  toolbarExtra,
}: Props) => (
  <>
    <section className="email-mkt__hero">
      <div className="email-mkt__hero-content">
        <div>
          <span className="email-mkt__eyebrow">{eyebrow}</span>
          <h1 className="email-mkt__title">{title}</h1>
          <p className="email-mkt__subtitle">{subtitle}</p>
        </div>
        <div className="email-mkt__stats">
          <div className="email-mkt__stat">
            <strong>{statsCount ?? templates.length}</strong>
            <span>{statsLabel}</span>
          </div>
        </div>
      </div>
    </section>

    <section className="email-mkt__panel">
      <div className="email-mkt__toolbar email-mkt__toolbar--split">
        <div className="email-mkt__segmented">
          {filters.map((filter) => (
            <button
              key={filter.id}
              type="button"
              className={activeFilter === filter.id ? "is-active" : ""}
              onClick={() => onFilterChange(filter.id)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        <div className="email-mkt__toolbar-actions">
          {toolbarExtra}
          <button type="button" className="email-mkt__primary-btn" onClick={onCreate}>
            {createLabel}
          </button>
        </div>
      </div>

      {templates.length === 0 ? (
        <p className="email-mkt__note">{emptyLabel}</p>
      ) : (
        <div className="email-mkt__template-grid">
          {templates.map((template) => (
            <article className="email-mkt__template-card" key={template.id}>
              <div
                className="email-mkt__template-thumb"
                style={{ borderColor: template.accent }}
              >
                <span style={{ background: template.accent }} />
              </div>
              <div>
                <strong>{template.name}</strong>
                <small>{template.updatedAt}</small>
              </div>
              <button
                type="button"
                className="email-mkt__ghost-btn"
                onClick={() => onTemplateAction(template.id)}
              >
                {actionLabel}
              </button>
            </article>
          ))}
        </div>
      )}
    </section>
  </>
);

export const emailTemplateLibraryFilters = {
  marketing: [
    { id: "all", label: <FormattedMessage id="app.emailMarketing.emails.tabs.all" /> },
    {
      id: "dragDrop",
      label: <FormattedMessage id="app.emailMarketing.emails.create.dragDrop" />,
    },
    {
      id: "html",
      label: <FormattedMessage id="app.emailMarketing.emails.create.html" />,
    },
    {
      id: "plain",
      label: <FormattedMessage id="app.emailMarketing.emails.create.plainText" />,
    },
  ],
  platform: [
    { id: "all", label: <FormattedMessage id="app.emailMarketing.emails.tabs.all" /> },
    {
      id: "published",
      label: <FormattedMessage id="app.platformAutomations.templates.filterPublished" />,
    },
    {
      id: "draft",
      label: <FormattedMessage id="app.platformAutomations.templates.filterDraft" />,
    },
  ],
  automations: [
    { id: "all", label: <FormattedMessage id="app.emailMarketing.emails.tabs.all" /> },
    {
      id: "published",
      label: <FormattedMessage id="app.platformAutomations.templates.filterPublished" />,
    },
    {
      id: "draft",
      label: <FormattedMessage id="app.platformAutomations.templates.filterDraft" />,
    },
    {
      id: "paused",
      label: <FormattedMessage id="app.platformAutomations.flows.filterPaused" />,
    },
  ],
};
